/**
 * Alert Rule Form Component
 * Form for creating and editing alert rules
 */

import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TextInput,
  ScrollView,
  TouchableOpacity,
  Switch,
  Alert as RNAlert,
  ActivityIndicator,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import gpsTrackingAPI from '../../api/services/gpsTracking';

interface AlertRuleFormProps {
  initialData?: any;
  onSubmit: (data: any) => void;
  onCancel: () => void;
  isEditing: boolean;
}

export default function AlertRuleForm({ initialData, onSubmit, onCancel, isEditing }: AlertRuleFormProps) {
  const [loading, setLoading] = useState(false);
  const [loadingTypes, setLoadingTypes] = useState(true);
  
  // Form state
  const [name, setName] = useState(initialData?.name || '');
  const [alertType, setAlertType] = useState(initialData?.alert_type || '');
  const [isActive, setIsActive] = useState(initialData?.is_active ?? true);
  const [devices, setDevices] = useState<number[]>(initialData?.devices || []);
  const [geofences, setGeofences] = useState<number[]>(initialData?.geofences || []);
  const [speedLimit, setSpeedLimit] = useState(initialData?.speed_limit?.toString() || '');
  const [offlineThreshold, setOfflineThreshold] = useState(initialData?.offline_threshold?.toString() || '10');
  const [gforceThreshold, setGforceThreshold] = useState(initialData?.gforce_threshold?.toString() || '0.4');
  const [speedDeltaThreshold, setSpeedDeltaThreshold] = useState(initialData?.speed_delta_threshold?.toString() || '30');
  const [idleThreshold, setIdleThreshold] = useState(initialData?.idle_threshold_minutes?.toString() || '10');
  const [drivingTimeThreshold, setDrivingTimeThreshold] = useState(initialData?.driving_time_threshold_hours?.toString() || '4');
  const [maintenanceKmThreshold, setMaintenanceKmThreshold] = useState(initialData?.maintenance_km_threshold?.toString() || '');
  const [lastMaintenanceKm, setLastMaintenanceKm] = useState(initialData?.last_maintenance_km?.toString() || '');
  const [fuelDropPercent, setFuelDropPercent] = useState(initialData?.fuel_drop_percent?.toString() || '20');
  const [cooldownMinutes, setCooldownMinutes] = useState(initialData?.cooldown_minutes?.toString() || '30');
  const [notificationChannels] = useState<string[]>(['email']); // Only email for now
  const [geofenceTriggerType, setGeofenceTriggerType] = useState(initialData?.geofence_trigger_type || 'both');
  const [ignitionTriggerType, setIgnitionTriggerType] = useState(initialData?.ignition_trigger_type || 'both');
  
  // Available options
  const [alertTypes, setAlertTypes] = useState<any>({priority: [], coming_soon: []});
  const [availableDevices, setAvailableDevices] = useState<any[]>([]);
  const [availableGeofences, setAvailableGeofences] = useState<any[]>([]);
  const [deviceSearch, setDeviceSearch] = useState('');
  const [devicePage, setDevicePage] = useState(1);
  const [deviceTotalPages, setDeviceTotalPages] = useState(1);
  const [deviceCount, setDeviceCount] = useState(0);
  const [deviceLoading, setDeviceLoading] = useState(false);
  const devicePageSize = 20;
  
  // Device selector
  const [showDeviceSelector, setShowDeviceSelector] = useState(false);
  const [allDevicesSelected, setAllDevicesSelected] = useState(devices.length === 0);
  
  // Geofence selector
  const [showGeofenceSelector, setShowGeofenceSelector] = useState(false);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (!showDeviceSelector) return;
    const timer = setTimeout(() => {
      loadDevices(devicePage, deviceSearch);
    }, 250);
    return () => clearTimeout(timer);
  }, [showDeviceSelector, devicePage, deviceSearch]);

  useEffect(() => {
    setDevicePage(1);
  }, [deviceSearch]);

  const loadInitialData = async () => {
    try {
      setLoadingTypes(true);
      const [types, geofencesList] = await Promise.all([
        gpsTrackingAPI.alertRules.getAlertTypes(),
        gpsTrackingAPI.geofences.listGeofences(),
      ]);
      
      setAlertTypes(types);
      setAvailableGeofences(geofencesList);
    } catch (error) {
      console.error('Failed to load initial data:', error);
      RNAlert.alert('Erro', 'Falha ao carregar dados iniciais');
    } finally {
      setLoadingTypes(false);
    }
  };

  const loadDevices = async (page: number, search: string) => {
    try {
      setDeviceLoading(true);
      const response = await gpsTrackingAPI.devices.searchDevices({
        page,
        page_size: devicePageSize,
        search: search || undefined,
      });
      setAvailableDevices(response.results);
      setDeviceTotalPages(response.total_pages || 1);
      setDeviceCount(response.count || 0);
    } catch (error) {
      console.error('Failed to load devices:', error);
      RNAlert.alert('Erro', 'Falha ao carregar veículos');
    } finally {
      setDeviceLoading(false);
    }
  };

  const handleSubmit = () => {
    // Validation
    if (!name.trim()) {
      RNAlert.alert('Erro', 'Nome da regra é obrigatório');
      return;
    }

    if (!alertType) {
      RNAlert.alert('Erro', 'Tipo de alerta é obrigatório');
      return;
    }

    // Validate speed limit for speed alerts
    if (alertType === 'speed_exceeded' && (!speedLimit || parseFloat(speedLimit) <= 0)) {
      RNAlert.alert('Erro', 'Limite de velocidade é obrigatório para alertas de excesso de velocidade');
      return;
    }

    // Validate geofences for geofence alerts
    if (alertType === 'geofence' && geofences.length === 0) {
      RNAlert.alert('Erro', 'Pelo menos uma geocerca é obrigatória para alertas de geocerca');
      return;
    }

    // Build submission data
    const data: any = {
      name: name.trim(),
      alert_type: alertType,
      is_active: isActive,
      notification_channels: notificationChannels,
      cooldown_minutes: parseInt(cooldownMinutes) || 30,
      devices: allDevicesSelected ? [] : devices,
      geofences: geofences,
    };

    // Add threshold fields based on alert type
    if (alertType === 'speed_exceeded') {
      data.speed_limit = parseFloat(speedLimit);
    }
    if (alertType === 'device_offline') {
      data.offline_threshold = parseInt(offlineThreshold) || 10;
    }
    if (alertType === 'harsh_braking' || alertType === 'harsh_acceleration' || alertType === 'harsh_cornering') {
      data.gforce_threshold = parseFloat(gforceThreshold) || 0.4;
      data.speed_delta_threshold = parseFloat(speedDeltaThreshold) || 30;
    }
    if (alertType === 'idle_excessive') {
      data.idle_threshold_minutes = parseInt(idleThreshold) || 10;
    }
    if (alertType === 'fatigue_driving') {
      data.driving_time_threshold_hours = parseFloat(drivingTimeThreshold) || 4;
    }
    if (alertType === 'maintenance_due') {
      data.maintenance_km_threshold = parseInt(maintenanceKmThreshold) || null;
      data.last_maintenance_km = parseInt(lastMaintenanceKm) || null;
    }
    if (alertType === 'fuel_drop') {
      data.fuel_drop_percent = parseFloat(fuelDropPercent) || 20;
    }
    
    // Add trigger type for geofence and ignition
    if (alertType === 'geofence') {
      data.geofence_trigger_type = geofenceTriggerType;
    }
    if (alertType === 'ignition') {
      data.ignition_trigger_type = ignitionTriggerType;
    }

    onSubmit(data);
  };

  const toggleDevice = (deviceId: number) => {
    if (devices.includes(deviceId)) {
      setDevices(devices.filter(id => id !== deviceId));
    } else {
      setDevices([...devices, deviceId]);
    }
    setAllDevicesSelected(false);
  };

  const toggleAllDevices = () => {
    if (allDevicesSelected) {
      setAllDevicesSelected(false);
      setDevices([]);
    } else {
      setAllDevicesSelected(true);
      setDevices([]);
    }
  };

  const toggleGeofence = (geofenceId: number) => {
    if (geofences.includes(geofenceId)) {
      setGeofences(geofences.filter(id => id !== geofenceId));
    } else {
      setGeofences([...geofences, geofenceId]);
    }
  };

  const getAlertTypeInfo = (typeCode: string) => {
    if (!alertTypes) return null;
    return (alertTypes.priority || []).find((t: any) => t.code === typeCode);
  };

  const selectedAlertTypeInfo = alertType ? getAlertTypeInfo(alertType) : null;

  if (loadingTypes) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#007AFF" />
        <Text style={styles.loadingText}>Carregando...</Text>
      </View>
    );
  }

  return (
    <ScrollView style={styles.container}>
      {/* Rule Name */}
      <View style={styles.formSection}>
        <Text style={styles.label}>Nome da Regra *</Text>
        <TextInput
          style={styles.input}
          value={name}
          onChangeText={setName}
          placeholder="Ex: Excesso de velocidade em zona urbana"
          placeholderTextColor="#C7C7CC"
        />
      </View>

      {/* Alert Type */}
      <View style={styles.formSection}>
        <Text style={styles.label}>Tipo de Alerta *</Text>
        
        <View style={styles.picker}>
          {alertTypes.priority?.map((type: any) => (
            <TouchableOpacity
              key={type.code}
              style={[
                styles.pickerOption,
                alertType === type.code && styles.pickerOptionSelected,
                !type.available && styles.pickerOptionDisabled
              ]}
              onPress={() => type.available && setAlertType(type.code)}
              disabled={!type.available}
            >
              <View style={styles.pickerOptionContent}>
                <View style={styles.pickerOptionTitleRow}>
                  <Text style={[
                    styles.pickerOptionText,
                    alertType === type.code && styles.pickerOptionTextSelected,
                    !type.available && styles.pickerOptionTextDisabled
                  ]}>
                    {type.label}
                  </Text>
                </View>
                {type.description && (
                  <Text style={styles.pickerOptionDescription}>
                    {type.description}
                  </Text>
                )}
              </View>
              {alertType === type.code && (
                <Ionicons name="checkmark-circle" size={20} color="#007AFF" />
              )}
            </TouchableOpacity>
          ))}
        </View>
      </View>

      {/* Threshold Fields (conditional) */}
      {alertType === 'speed_exceeded' && (
        <View style={styles.formSection}>
          <Text style={styles.label}>Limite de Velocidade (km/h) *</Text>
          <TextInput
            style={styles.input}
            value={speedLimit}
            onChangeText={setSpeedLimit}
            placeholder="Ex: 80"
            keyboardType="numeric"
            placeholderTextColor="#C7C7CC"
          />
        </View>
      )}

      {alertType === 'device_offline' && (
        <View style={styles.formSection}>
          <Text style={styles.label}>Tempo Offline (minutos)</Text>
          <TextInput
            style={styles.input}
            value={offlineThreshold}
            onChangeText={setOfflineThreshold}
            placeholder="10"
            keyboardType="numeric"
            placeholderTextColor="#C7C7CC"
          />
        </View>
      )}

      {(alertType === 'harsh_braking' || alertType === 'harsh_acceleration' || alertType === 'harsh_cornering') && (
        <>
          <View style={styles.formSection}>
            <Text style={styles.label}>Limiar de Força G</Text>
            <TextInput
              style={styles.input}
              value={gforceThreshold}
              onChangeText={setGforceThreshold}
              placeholder="0.4"
              keyboardType="numeric"
              placeholderTextColor="#C7C7CC"
            />
            <Text style={styles.hint}>Recomendado: 0.4g para eventos bruscos</Text>
          </View>
          <View style={styles.formSection}>
            <Text style={styles.label}>Variação de Velocidade (km/h)</Text>
            <TextInput
              style={styles.input}
              value={speedDeltaThreshold}
              onChangeText={setSpeedDeltaThreshold}
              placeholder="30"
              keyboardType="numeric"
              placeholderTextColor="#C7C7CC"
            />
          </View>
        </>
      )}

      {alertType === 'idle_excessive' && (
        <View style={styles.formSection}>
          <Text style={styles.label}>Tempo de Inatividade (minutos)</Text>
          <TextInput
            style={styles.input}
            value={idleThreshold}
            onChangeText={setIdleThreshold}
            placeholder="10"
            keyboardType="numeric"
            placeholderTextColor="#C7C7CC"
          />
        </View>
      )}

      {alertType === 'fatigue_driving' && (
        <View style={styles.formSection}>
          <Text style={styles.label}>Tempo de Condução (horas)</Text>
          <TextInput
            style={styles.input}
            value={drivingTimeThreshold}
            onChangeText={setDrivingTimeThreshold}
            placeholder="4"
            keyboardType="numeric"
            placeholderTextColor="#C7C7CC"
          />
        </View>
      )}

      {alertType === 'fuel_drop' && (
        <View style={styles.formSection}>
          <Text style={styles.label}>Queda de Combustível (%)</Text>
          <TextInput
            style={styles.input}
            value={fuelDropPercent}
            onChangeText={setFuelDropPercent}
            placeholder="20"
            keyboardType="numeric"
            placeholderTextColor="#C7C7CC"
          />
        </View>
      )}

      {alertType === 'maintenance_due' && (
        <>
          <View style={styles.formSection}>
            <Text style={styles.label}>KM até Manutenção</Text>
            <TextInput
              style={styles.input}
              value={maintenanceKmThreshold}
              onChangeText={setMaintenanceKmThreshold}
              placeholder="10000"
              keyboardType="numeric"
              placeholderTextColor="#C7C7CC"
            />
          </View>
          <View style={styles.formSection}>
            <Text style={styles.label}>KM na Última Manutenção</Text>
            <TextInput
              style={styles.input}
              value={lastMaintenanceKm}
              onChangeText={setLastMaintenanceKm}
              placeholder="50000"
              keyboardType="numeric"
              placeholderTextColor="#C7C7CC"
            />
          </View>
        </>
      )}

      {/* Devices */}
      <View style={styles.formSection}>
        <Text style={styles.label}>Veículos</Text>
        <TouchableOpacity
          style={styles.selectorButton}
          onPress={toggleAllDevices}
        >
          <Ionicons 
            name={allDevicesSelected ? 'checkbox' : 'square-outline'} 
            size={20} 
            color="#007AFF" 
          />
          <Text style={styles.selectorButtonText}>
            Todos os veículos
          </Text>
        </TouchableOpacity>
        
        {!allDevicesSelected && (
          <>
            <TouchableOpacity
              style={styles.expandButton}
              onPress={() => setShowDeviceSelector(!showDeviceSelector)}
            >
              <Text style={styles.expandButtonText}>
                {devices.length} veículo(s) selecionado(s)
              </Text>
              <Ionicons 
                name={showDeviceSelector ? 'chevron-up' : 'chevron-down'} 
                size={20} 
                color="#8E8E93" 
              />
            </TouchableOpacity>
            
            {showDeviceSelector && (
              <View style={styles.optionsList}>
                <View style={styles.searchRow}>
                  <Ionicons name="search" size={16} color="#8E8E93" />
                  <TextInput
                    style={styles.searchInput}
                    value={deviceSearch}
                    onChangeText={setDeviceSearch}
                    placeholder="Procurar veículo ou IMEI"
                    placeholderTextColor="#C7C7CC"
                  />
                </View>

                <Text style={styles.searchMeta}>
                  {deviceCount} veículo(s) encontrado(s)
                </Text>

                {deviceLoading ? (
                  <View style={styles.loadingInline}>
                    <ActivityIndicator size="small" color="#007AFF" />
                    <Text style={styles.loadingInlineText}>Carregando...</Text>
                  </View>
                ) : (
                  <ScrollView style={styles.deviceList}>
                    {availableDevices.map((device) => (
                      <TouchableOpacity
                        key={device.id}
                        style={styles.optionItem}
                        onPress={() => toggleDevice(device.id)}
                      >
                        <Ionicons 
                          name={devices.includes(device.id) ? 'checkbox' : 'square-outline'} 
                          size={20} 
                          color="#007AFF" 
                        />
                        <View style={styles.optionTextGroup}>
                          <Text style={styles.optionText}>{device.label}</Text>
                          {device.current_vehicle?.registration_number && (
                            <Text style={styles.optionSubtext}>
                              {device.current_vehicle.registration_number}
                            </Text>
                          )}
                        </View>
                      </TouchableOpacity>
                    ))}
                  </ScrollView>
                )}

                <View style={styles.paginationRow}>
                  <TouchableOpacity
                    style={[
                      styles.paginationButton,
                      devicePage <= 1 && styles.paginationButtonDisabled,
                    ]}
                    disabled={devicePage <= 1}
                    onPress={() => setDevicePage((prev) => Math.max(prev - 1, 1))}
                  >
                    <Ionicons name="chevron-back" size={16} color="#007AFF" />
                    <Text style={styles.paginationText}>Anterior</Text>
                  </TouchableOpacity>
                  <Text style={styles.paginationStatus}>
                    {devicePage} / {deviceTotalPages || 1}
                  </Text>
                  <TouchableOpacity
                    style={[
                      styles.paginationButton,
                      devicePage >= deviceTotalPages && styles.paginationButtonDisabled,
                    ]}
                    disabled={devicePage >= deviceTotalPages}
                    onPress={() => setDevicePage((prev) => Math.min(prev + 1, deviceTotalPages))}
                  >
                    <Text style={styles.paginationText}>Próximo</Text>
                    <Ionicons name="chevron-forward" size={16} color="#007AFF" />
                  </TouchableOpacity>
                </View>
              </View>
            )}
          </>
        )}
      </View>

      {/* Geofence Trigger Type */}
      {alertType === 'geofence' && (
        <View style={styles.formSection}>
          <Text style={styles.label}>Quando alertar? *</Text>
          <View style={styles.optionsList}>
            <TouchableOpacity
              style={styles.optionItem}
              onPress={() => setGeofenceTriggerType('enter')}
            >
              <Ionicons 
                name={geofenceTriggerType === 'enter' ? 'radio-button-on' : 'radio-button-off'} 
                size={20} 
                color="#007AFF" 
              />
              <Text style={styles.optionText}>Ao entrar na área</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.optionItem}
              onPress={() => setGeofenceTriggerType('exit')}
            >
              <Ionicons 
                name={geofenceTriggerType === 'exit' ? 'radio-button-on' : 'radio-button-off'} 
                size={20} 
                color="#007AFF" 
              />
              <Text style={styles.optionText}>Ao sair da área</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.optionItem}
              onPress={() => setGeofenceTriggerType('both')}
            >
              <Ionicons 
                name={geofenceTriggerType === 'both' ? 'radio-button-on' : 'radio-button-off'} 
                size={20} 
                color="#007AFF" 
              />
              <Text style={styles.optionText}>Entrada e saída</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

      {/* Ignition Trigger Type */}
      {alertType === 'ignition' && (
        <View style={styles.formSection}>
          <Text style={styles.label}>Quando alertar? *</Text>
          <View style={styles.optionsList}>
            <TouchableOpacity
              style={styles.optionItem}
              onPress={() => setIgnitionTriggerType('on')}
            >
              <Ionicons 
                name={ignitionTriggerType === 'on' ? 'radio-button-on' : 'radio-button-off'} 
                size={20} 
                color="#007AFF" 
              />
              <Text style={styles.optionText}>Ao ligar</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.optionItem}
              onPress={() => setIgnitionTriggerType('off')}
            >
              <Ionicons 
                name={ignitionTriggerType === 'off' ? 'radio-button-on' : 'radio-button-off'} 
                size={20} 
                color="#007AFF" 
              />
              <Text style={styles.optionText}>Ao desligar</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.optionItem}
              onPress={() => setIgnitionTriggerType('both')}
            >
              <Ionicons 
                name={ignitionTriggerType === 'both' ? 'radio-button-on' : 'radio-button-off'} 
                size={20} 
                color="#007AFF" 
              />
              <Text style={styles.optionText}>Ligar e desligar</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

      {/* Geofences (for geofence alerts) */}
      {alertType === 'geofence' && (
        <View style={styles.formSection}>
          <Text style={styles.label}>Geocercas *</Text>
          <TouchableOpacity
            style={styles.expandButton}
            onPress={() => setShowGeofenceSelector(!showGeofenceSelector)}
          >
            <Text style={styles.expandButtonText}>
              {geofences.length} geocerca(s) selecionada(s)
            </Text>
            <Ionicons 
              name={showGeofenceSelector ? 'chevron-up' : 'chevron-down'} 
              size={20} 
              color="#8E8E93" 
            />
          </TouchableOpacity>
          
          {showGeofenceSelector && (
            <View style={styles.optionsList}>
              {availableGeofences.map((geofence) => (
                <TouchableOpacity
                  key={geofence.id}
                  style={styles.optionItem}
                  onPress={() => toggleGeofence(geofence.id)}
                >
                  <Ionicons 
                    name={geofences.includes(geofence.id) ? 'checkbox' : 'square-outline'} 
                    size={20} 
                    color="#007AFF" 
                  />
                  <Text style={styles.optionText}>{geofence.name}</Text>
                </TouchableOpacity>
              ))}
            </View>
          )}
        </View>
      )}

      {/* Email Note */}
      <View style={styles.infoBox}>
        <Ionicons name="mail-outline" size={20} color="#007AFF" />
        <Text style={styles.infoText}>
          Os alertas serão enviados para o email do proprietário da empresa
        </Text>
      </View>

      {/* Cooldown */}
      <View style={styles.formSection}>
        <Text style={styles.label}>Intervalo entre Alertas (minutos)</Text>
        <TextInput
          style={styles.input}
          value={cooldownMinutes}
          onChangeText={setCooldownMinutes}
          placeholder="30"
          keyboardType="numeric"
          placeholderTextColor="#C7C7CC"
        />
        <Text style={styles.hint}>Tempo mínimo entre alertas do mesmo tipo</Text>
      </View>

      {/* Active Switch */}
      <View style={styles.formSection}>
        <View style={styles.switchRow}>
          <Text style={styles.label}>Regra Ativa</Text>
          <Switch
            value={isActive}
            onValueChange={setIsActive}
            trackColor={{ false: '#E5E5EA', true: '#34C759' }}
            thumbColor="#FFFFFF"
          />
        </View>
      </View>

      {/* Action Buttons */}
      <View style={styles.actions}>
        <TouchableOpacity style={styles.cancelButton} onPress={onCancel}>
          <Text style={styles.cancelButtonText}>Cancelar</Text>
        </TouchableOpacity>
        <TouchableOpacity 
          style={styles.submitButton} 
          onPress={handleSubmit}
          disabled={loading}
          testID="alert-rule-submit"
        >
          {loading ? (
            <ActivityIndicator color="#FFFFFF" />
          ) : (
            <Text style={styles.submitButtonText}>
              {isEditing ? 'Atualizar' : 'Criar'} Regra
            </Text>
          )}
        </TouchableOpacity>
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 16,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32,
  },
  loadingText: {
    marginTop: 12,
    fontSize: 14,
    color: '#8E8E93',
  },
  formSection: {
    marginBottom: 20,
  },
  label: {
    fontSize: 15,
    fontWeight: '600',
    color: '#000000',
    marginBottom: 8,
  },
  input: {
    borderWidth: 1,
    borderColor: '#E5E5EA',
    borderRadius: 8,
    padding: 12,
    fontSize: 15,
    color: '#000000',
    backgroundColor: '#FFFFFF',
  },
  textArea: {
    minHeight: 60,
    textAlignVertical: 'top',
  },
  hint: {
    fontSize: 12,
    color: '#8E8E93',
    marginTop: 4,
    fontStyle: 'italic',
  },
  picker: {
    borderWidth: 1,
    borderColor: '#E5E5EA',
    borderRadius: 8,
    backgroundColor: '#FFFFFF',
  },
  pickerOption: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E5EA',
  },
  pickerOptionSelected: {
    backgroundColor: '#E5F1FF',
  },
  pickerOptionContent: {
    flex: 1,
  },
  pickerOptionText: {
    fontSize: 15,
    color: '#000000',
  },
  pickerOptionTextSelected: {
    fontWeight: '600',
    color: '#007AFF',
  },
  pickerOptionDescription: {
    fontSize: 12,
    color: '#8E8E93',
    marginTop: 2,
  },
  pickerOptionDisabled: {
    opacity: 0.5,
    backgroundColor: '#F9F9F9',
  },
  pickerOptionTextDisabled: {
    color: '#8E8E93',
  },
  pickerOptionTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  infoBox: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 12,
    backgroundColor: '#E5F1FF',
    borderRadius: 8,
    marginBottom: 20,
  },
  infoText: {
    flex: 1,
    fontSize: 13,
    color: '#007AFF',
  },
  selectorButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    padding: 12,
    borderWidth: 1,
    borderColor: '#E5E5EA',
    borderRadius: 8,
    backgroundColor: '#FFFFFF',
    marginBottom: 8,
  },
  selectorButtonText: {
    fontSize: 15,
    color: '#000000',
  },
  expandButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 12,
    borderWidth: 1,
    borderColor: '#E5E5EA',
    borderRadius: 8,
    backgroundColor: '#F9F9F9',
  },
  expandButtonText: {
    fontSize: 14,
    color: '#000000',
  },
  optionsList: {
    marginTop: 8,
    borderWidth: 1,
    borderColor: '#E5E5EA',
    borderRadius: 8,
    backgroundColor: '#FFFFFF',
    maxHeight: 200,
    padding: 8,
  },
  searchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 8,
    paddingVertical: 6,
    borderWidth: 1,
    borderColor: '#E5E5EA',
    borderRadius: 8,
    backgroundColor: '#F9F9F9',
    marginBottom: 8,
  },
  searchInput: {
    flex: 1,
    fontSize: 14,
    color: '#000000',
  },
  searchMeta: {
    fontSize: 12,
    color: '#8E8E93',
    marginBottom: 8,
  },
  optionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E5EA',
  },
  optionTextGroup: {
    flex: 1,
  },
  optionText: {
    fontSize: 14,
    color: '#000000',
  },
  optionSubtext: {
    fontSize: 12,
    color: '#8E8E93',
    marginTop: 2,
  },
  loadingInline: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 12,
    justifyContent: 'center',
  },
  loadingInlineText: {
    fontSize: 12,
    color: '#8E8E93',
  },
  deviceList: {
    maxHeight: 120,
  },
  paginationRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: 8,
  },
  paginationButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 6,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#E5E5EA',
    backgroundColor: '#FFFFFF',
  },
  paginationButtonDisabled: {
    opacity: 0.5,
  },
  paginationText: {
    fontSize: 12,
    color: '#007AFF',
    fontWeight: '600',
  },
  paginationStatus: {
    fontSize: 12,
    color: '#8E8E93',
  },
  switchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  actions: {
    flexDirection: 'row',
    gap: 12,
    paddingVertical: 16,
  },
  cancelButton: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E5E5EA',
    alignItems: 'center',
  },
  cancelButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#8E8E93',
  },
  submitButton: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 8,
    backgroundColor: '#007AFF',
    alignItems: 'center',
  },
  submitButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
  },
});
